// src/pages/admin/AdminCareerApply.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  getAllJobApplications, deleteJobApplication, getJobs,
} from "../../services/firestoreService";
import Spinner from "../../components/common/Spinner";
import Modal from "../../components/common/Modal";
import { format } from "date-fns";
import { FiTrash2, FiUsers, FiMail, FiPhone, FiExternalLink, FiBriefcase } from "react-icons/fi";
import toast from "react-hot-toast";

export default function AdminCareerApply() {
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [jobFilter, setJobFilter] = useState("All");

  const load = async () => {
    const [apps, jobList] = await Promise.all([getAllJobApplications(), getJobs()]);
    setApplications(apps);
    setJobs(jobList);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Map jobId -> job title, so the table can show a readable title
  // instead of a raw Firestore document id.
  const jobTitleById = useMemo(() => {
    const map = {};
    jobs.forEach((j) => { map[j.id] = j.title; });
    return map;
  }, [jobs]);

  const jobTitleOptions = useMemo(() => {
    const set = new Set(applications.map((a) => jobTitleById[a.jobId] || "Unknown Role"));
    return ["All", ...Array.from(set)];
  }, [applications, jobTitleById]);

  const filteredApplications = jobFilter === "All"
    ? applications
    : applications.filter((a) => (jobTitleById[a.jobId] || "Unknown Role") === jobFilter);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    await deleteJobApplication(id);
    toast.success("Application deleted");
    setApplications((prev) => prev.filter((a) => a.id !== id));
    setSelected(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Career Applications</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {applications.length} application{applications.length !== 1 ? "s" : ""} received
          </p>
        </div>

        {jobTitleOptions.length > 1 && (
          <select
            value={jobFilter}
            onChange={(e) => setJobFilter(e.target.value)}
            className="input w-auto text-sm"
          >
            {jobTitleOptions.map((title) => (
              <option key={title} value={title}>{title}</option>
            ))}
          </select>
        )}
      </div>

      {loading ? <Spinner center /> : (
        <div className="card overflow-hidden">
          {filteredApplications.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <FiUsers className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>No applications yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {["Applicant", "Applied For", "Contact", "Resume", "Date", "Actions"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredApplications.map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelected(app)}>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">{app.fullName}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <span className="inline-flex items-center gap-1.5">
                          <FiBriefcase className="w-3.5 h-3.5 text-primary-500 flex-shrink-0" />
                          {jobTitleById[app.jobId] || "Unknown Role"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <div className="flex flex-col gap-0.5">
                          <span>{app.email}</span>
                          <span className="text-xs text-gray-400">{app.phone}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {app.resumeLink ? (
                          <a
                            href={app.resumeLink}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-primary-600 hover:underline text-sm"
                          >
                            View <FiExternalLink className="w-3.5 h-3.5" />
                          </a>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {app.createdAt?.toDate ? format(app.createdAt.toDate(), "dd MMM yyyy") : "—"}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => handleDelete(app.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors">
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Application detail modal */}
      <Modal isOpen={!!selected} onClose={() => setSelected(null)} title="Application Details">
        {selected && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Full Name:</span>
                <span className="font-medium ml-2">{selected.fullName}</span>
              </div>
              <div>
                <span className="text-gray-500">Applied For:</span>
                <span className="font-medium ml-2">{jobTitleById[selected.jobId] || "Unknown Role"}</span>
              </div>
              <div className="flex items-center gap-2">
                <FiMail className="w-3.5 h-3.5 text-gray-400" />
                <a href={`mailto:${selected.email}`} className="font-medium text-primary-600 hover:underline">
                  {selected.email}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <FiPhone className="w-3.5 h-3.5 text-gray-400" />
                <span className="font-medium">{selected.phone}</span>
              </div>
              <div className="col-span-1 sm:col-span-2">
                <span className="text-gray-500">Applied on:</span>
                <span className="ml-2">
                  {selected.createdAt?.toDate ? format(selected.createdAt.toDate(), "dd MMMM yyyy, hh:mm a") : "—"}
                </span>
              </div>
            </div>

            {selected.resumeLink && (
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">Resume</p>
                <a
                  href={selected.resumeLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-primary-600 hover:underline text-sm bg-gray-50 rounded-lg px-3 py-2"
                >
                  {selected.resumeLink} <FiExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                </a>
              </div>
            )}

            {selected.coverNote && (
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">Cover Note</p>
                <p className="text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap leading-relaxed">
                  {selected.coverNote}
                </p>
              </div>
            )}


          </div>
        )}
      </Modal>
    </div>
  );
}